import { useEffect, useReducer, useState } from "react";
import { debounce } from "shared/utils";

function isMobile() {
  const regex =
    /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  return regex.test(navigator.userAgent);
}

const initialBreakpoints = {
  xs: false,
  sm: false,
  md: false,
  lg: false,
  xl: false,
  xxl: false,
};

function breakpointsReducer(_, width) {
  if (width < 576) return { ...initialBreakpoints, xs: true };
  else if (width >= 576) return { ...initialBreakpoints, sm: true };
  else if (width >= 768) return { ...initialBreakpoints, md: true };
  else if (width >= 992) return { ...initialBreakpoints, lg: true };
  else if (width >= 1200) return { ...initialBreakpoints, xl: true };
  else if (width >= 1400) return { ...initialBreakpoints, xxl: true };
}

/**
 * Hook to track window breakpoints and dimensions.
 * @returns {{
 *    breakpoints: {
 *      xs: boolean,
 *      sm: boolean,
 *      md: boolean,
 *      lg: boolean,
 *      xl: boolean,
 *      xxl: boolean
 *    },
 *    dimension: number[],
 *    isMobileDevice: boolean
 *  }} Object containing breakpoints and window dimensions.
 */
export function useBreakpoints() {
  const [breakpoints, dispatch] = useReducer(
    /**
     * Reducer function to update breakpoints based on window width.
     * @param {Object} _ - Previous state (not used in this case).
     * @param {number} width - Window width.
     * @returns {Object} Updated breakpoints.
     */
    breakpointsReducer,
    initialBreakpoints
  );

  const [dimension, setDimension] = useState([
    window.innerWidth,
    window.innerHeight,
  ]);

  const [isMobileDevice] = useState(() => isMobile());

  useEffect(() => {
    /**
     * Handles window resize event and updates dimensions and breakpoints.
     */
    const debouncedResizeHandler = debounce(() => {
      setDimension([window.innerWidth, window.innerHeight]);
      // @ts-ignore
      dispatch(window.innerWidth);
    }, 200);

    debouncedResizeHandler();
    window.addEventListener("resize", debouncedResizeHandler);
    return () => window.removeEventListener("resize", debouncedResizeHandler);
  }, []);

  return { breakpoints, dimension, isMobileDevice };
}
